import type { WbsFlatItem } from '../types';
import { formatProjectDateOnly } from 'src/utils/date';

type ComparisonEntry = { value: number | null; actedAt: string | null };

function toFiniteNumber(v: unknown): number | null {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
}

function getWeight(row: WbsFlatItem) {
    const n = toFiniteNumber((row as any).actualWeight ?? (row as any).weight ?? 0);
    return n != null && n > 0 ? n : 0;
}

function buildChildrenMap(rows: WbsFlatItem[]) {
    const map = new Map<string, string[]>();
    for (const row of rows) {
        if (!map.has(row.id)) map.set(row.id, []);
        if (!row.parentId) continue;
        const children = map.get(row.parentId) ?? [];
        children.push(row.id);
        map.set(row.parentId, children);
    }
    return map;
}

export function getComparisonValue(entry: ComparisonEntry | null | undefined) {
    return toFiniteNumber(entry?.value ?? null);
}

export function hasWeekMinusOnePending(weeklyProgressComparison?: any | null) {
    const provisional = (weeklyProgressComparison?.n1?.provisional ?? {}) as Record<string, ComparisonEntry | undefined>;
    const approved = (weeklyProgressComparison?.n1?.approved ?? {}) as Record<string, ComparisonEntry | undefined>;

    return Object.keys(provisional).some((nodeId) => {
        const prov = getComparisonValue(provisional[nodeId]);
        const appr = getComparisonValue(approved[nodeId]);
        return prov != null && appr == null;
    });
}

export function getWeekMinusOneColumnLabel(weeklyProgressComparison?: any | null) {
    return hasWeekMinusOnePending(weeklyProgressComparison) ? '잠정(W-1주)' : '확정(W-1주)';
}

function toDayStart(dateLike: string | Date | null | undefined) {
    if (!dateLike) return null;
    const d =
        typeof dateLike === 'string'
            ? new Date(`${formatProjectDateOnly(dateLike, 'Asia/Seoul', '')}T00:00:00`)
            : new Date(dateLike.getFullYear(), dateLike.getMonth(), dateLike.getDate());
    return Number.isNaN(d.getTime()) ? null : d;
}

export function getCompletedCarryForWeek(row: WbsFlatItem, weekDate: Date, currentValue?: number | null) {
    const completionSignal = toFiniteNumber(currentValue ?? row.liveEarnedValue ?? row.earnedValue ?? null);
    if (completionSignal == null || completionSignal < 100) return null;

    const finish = toDayStart(row.plannedFinish ?? null);
    if (!finish) return 100;

    const weekStart = toDayStart(weekDate);
    if (!weekStart) return null;

    return finish.getTime() <= weekStart.getTime() ? 100 : null;
}

export function buildWeightedValueMap(
    rows: WbsFlatItem[],
    resolver: (row: WbsFlatItem) => number | null | undefined,
): Record<string, number | null> {
    const rowMap = new Map(rows.map((row) => [row.id, row] as const));
    const childrenMap = buildChildrenMap(rows);
    const memo = new Map<string, { weightedSum: number; totalWeight: number; hasAny: boolean }>();

    const visit = (rowId: string): { weightedSum: number; totalWeight: number; hasAny: boolean } => {
        const cached = memo.get(rowId);
        if (cached) return cached;

        const row = rowMap.get(rowId);
        if (!row) {
            const empty = { weightedSum: 0, totalWeight: 0, hasAny: false };
            memo.set(rowId, empty);
            return empty;
        }

        const childIds = childrenMap.get(rowId) ?? [];
        if (childIds.length === 0) {
            const weight = getWeight(row);
            const value = toFiniteNumber(resolver(row));
            const leaf =
                value != null && weight > 0
                    ? { weightedSum: value * weight, totalWeight: weight, hasAny: true }
                    : { weightedSum: 0, totalWeight: 0, hasAny: false };
            memo.set(rowId, leaf);
            return leaf;
        }

        let weightedSum = 0;
        let totalWeight = 0;
        let hasAny = false;

        for (const childId of childIds) {
            const child = visit(childId);
            weightedSum += child.weightedSum;
            totalWeight += child.totalWeight;
            if (child.hasAny) hasAny = true;
        }

        const parent = { weightedSum, totalWeight, hasAny };
        memo.set(rowId, parent);
        return parent;
    };

    const out: Record<string, number | null> = {};
    for (const row of rows) {
        const value = visit(row.id);
        out[row.id] = value.hasAny && value.totalWeight > 0 ? value.weightedSum / value.totalWeight : null;
    }
    return out;
}
